/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package utils

import kotlinx.datetime.*

object DateUtils {
    
    fun calculatePeriodLabel(timeIntervalMode: String, periodOffset: Int): String {
        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
        
        return when (timeIntervalMode) {
            "Weekly" -> {
                if (periodOffset == 0) {
                    "This Week"
                } else {
                    val targetDate = today.plus(periodOffset, DateTimeUnit.WEEK)
                    val startOfWeek = targetDate.minus((targetDate.dayOfWeek.isoDayNumber - 1), DateTimeUnit.DAY)
                    val endOfWeek = startOfWeek.plus(6, DateTimeUnit.DAY)
                    
                    val monthFormat = startOfWeek.month.name.take(3)
                    val endMonthFormat = endOfWeek.month.name.take(3)
                    
                    if (startOfWeek.month == endOfWeek.month) {
                        "$monthFormat ${startOfWeek.dayOfMonth} - ${endOfWeek.dayOfMonth} ${endOfWeek.year}"
                    } else {
                        "$monthFormat ${startOfWeek.dayOfMonth} - $endMonthFormat ${endOfWeek.dayOfMonth} ${endOfWeek.year}"
                    }
                }
            }
            
            "Quarterly" -> {
                if (periodOffset == 0) {
                    "This Quarter"
                } else {
                    val targetDate = today.plus(periodOffset * 3, DateTimeUnit.MONTH)
                    val quarter = (targetDate.monthNumber - 1) / 3 + 1
                    "Q$quarter ${targetDate.year}"
                }
            }
            
            else -> { // Monthly
                val targetDate = today.plus(periodOffset, DateTimeUnit.MONTH)
                "${targetDate.month.name.take(3)} ${targetDate.year}"
            }
        }
    }

    fun calculateDateRange(timeIntervalMode: String, periodOffset: Int): Pair<LocalDate, LocalDate> {
        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
        
        return when (timeIntervalMode) {
            "Weekly" -> {
                val targetDate = today.plus(periodOffset, DateTimeUnit.WEEK)
                val startOfWeek = targetDate.minus((targetDate.dayOfWeek.isoDayNumber - 1), DateTimeUnit.DAY)
                val endOfWeek = startOfWeek.plus(6, DateTimeUnit.DAY)
                Pair(startOfWeek, endOfWeek)
            }
            
            "Quarterly" -> {
                val targetDate = today.plus(periodOffset * 3, DateTimeUnit.MONTH)
                val quarterStartMonth = ((targetDate.monthNumber - 1) / 3) * 3 + 1
                val startOfQuarter = LocalDate(targetDate.year, quarterStartMonth, 1)
                val endOfQuarter = startOfQuarter.plus(3, DateTimeUnit.MONTH).minus(1, DateTimeUnit.DAY)
                Pair(startOfQuarter, endOfQuarter)
            }
            
            else -> { // Monthly
                val targetDate = today.plus(periodOffset, DateTimeUnit.MONTH)
                val startOfMonth = LocalDate(targetDate.year, targetDate.month, 1)
                val endOfMonth = startOfMonth.plus(1, DateTimeUnit.MONTH).minus(1, DateTimeUnit.DAY)
                Pair(startOfMonth, endOfMonth)
            }
        }
    }

    fun getPreviousButtonText(timeIntervalMode: String): String = "Prev"
    fun getNextButtonText(timeIntervalMode: String): String = "Next"

}